// src/app/pages/visitor-analytics/visitor-analytics.component.ts

import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subject, takeUntil } from 'rxjs';

import {
  ActiveVisitorsNow,
  RecentVisitor,
  TopVisitorPath,
  VisitorNavigationTrace,
  VisitorSummary,
  VisitorTimelineItem,
} from '../../models/admin-visitors.model';
import { AdminVisitorsService } from 'services/admin-visitors.service';

interface PageSearchVisitor extends Partial<RecentVisitor> {
  visitorId: string;
  visitorKey?: string;

  matchedPath?: string;
  matchedTitle?: string;
  matchedFullUrl?: string;

  firstMatchedAt?: string;
  lastMatchedAt?: string;
  matchesCount?: number;

  source?: string;
  device?: string;
  ip?: string;

  firstSeenAt?: string | null;
  lastSeenAt?: string | null;

  requestsCount?: number;
  visitsCount?: number;

  visitorType?: 'new' | 'returning' | 'unknown' | string;
  userType?: string;
  userId?: string | null;

  firstPath?: string;
  lastPath?: string;
}

type VisitorExplorerItem = RecentVisitor | PageSearchVisitor;

@Component({
  selector: 'app-visitor-analytics',
  templateUrl: './visitor-analytics.component.html',
  styleUrls: ['./visitor-analytics.component.scss'],
})
export class VisitorAnalyticsComponent implements OnInit, OnDestroy {
  loading = false;
  errorMessage = '';

  from = '';
  to = '';

  summary: VisitorSummary | null = null;
  timeline: VisitorTimelineItem[] = [];
  recentVisitors: RecentVisitor[] = [];
  allVisitors: RecentVisitor[] = [];
  topPaths: TopVisitorPath[] = [];
  activeNow: ActiveVisitorsNow | null = null;

  behaviorInsights: any | null = null;

  visitorSearchTerm = '';

  traceLoading = false;
  traceError = '';
  traceModalOpen = false;
  selectedTrace: VisitorNavigationTrace | null = null;

  pageSearchLoading = false;
  pageSearchActive = false;
  pageSearchLabel = '';
  pageSearchResults: PageSearchVisitor[] = [];
  pageSearchError = '';

  private destroy$ = new Subject<void>();

  constructor(private adminVisitorsService: AdminVisitorsService) {}

  ngOnInit(): void {
    this.setDefaultDateRange();
    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDashboard(): void {
    this.loading = true;
    this.errorMessage = '';

    this.resetPageSearch();

    const query = {
      from: this.from,
      to: this.to,
    };

    forkJoin({
      summary: this.adminVisitorsService.getSummary(query),
      timeline: this.adminVisitorsService.getTimeline(query),
      recent: this.adminVisitorsService.getRecent({ ...query, limit: 12 }),
      allVisitors: this.adminVisitorsService.getRecent({ ...query, limit: 100 }),
      topPaths: this.adminVisitorsService.getTopPaths({ ...query, limit: 8 }),
      activeNow: this.adminVisitorsService.getActiveNow({ minutes: 10 }),
      behavior: this.adminVisitorsService.getBehaviorInsights({
        ...query,
        sessionTimeoutMinutes: 30,
        maxTimeOnPageSeconds: 1800,
      }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.summary = response.summary.data;
          this.timeline = response.timeline.data || [];
          this.recentVisitors = response.recent.data || [];
          this.allVisitors = response.allVisitors.data || [];
          this.topPaths = response.topPaths.data || [];
          this.activeNow = response.activeNow.data;
          this.behaviorInsights = response.behavior.data || null;

          this.loading = false;
        },
        error: (error) => {
          console.error('Error cargando analítica de visitantes:', error);
          this.errorMessage = 'No se pudo cargar la analítica de visitantes.';
          this.loading = false;
        },
      });
  }

  refresh(): void {
    this.loadDashboard();
  }

  searchVisitorTrace(): void {
    const term = this.visitorSearchTerm.trim();

    this.traceError = '';
    this.pageSearchError = '';

    if (!term) {
      this.resetPageSearch();
      this.traceError =
        'Introduce un visitorId o una ruta. Ejemplo: /dashboard-add-property';
      return;
    }

    if (this.isPageSearchTerm(term)) {
      this.searchByPagePath(term);
      return;
    }

    const exactVisitor = this.allVisitors.find((visitor) => {
      return visitor.visitorId === term || visitor.visitorKey === term;
    });

    if (exactVisitor) {
      this.openVisitorTrace(exactVisitor);
      return;
    }

    const firstFilteredVisitor = this.getFilteredVisitors()[0];

    if (firstFilteredVisitor) {
      this.openVisitorTrace(firstFilteredVisitor);
      return;
    }

    this.traceError = 'No se encontró ningún visitante con ese criterio.';
  }

  searchByPagePath(path: string): void {
    const cleanPath = this.normalizePageSearchTerm(path);

    if (!cleanPath) {
      this.resetPageSearch();
      this.pageSearchError = 'Introduce una ruta válida.';
      return;
    }

    this.pageSearchLoading = true;
    this.pageSearchActive = true;
    this.pageSearchLabel = cleanPath;
    this.pageSearchResults = [];
    this.pageSearchError = '';
    this.traceError = '';

    this.adminVisitorsService
      .searchVisitorsByPagePath({
        path: cleanPath,
        from: this.from,
        to: this.to,
        limit: 150,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const data = response?.data || response || {};

          this.pageSearchResults = Array.isArray(data.items) ? data.items : [];
          this.pageSearchLabel = data.search || cleanPath;

          if (!this.pageSearchResults.length) {
            this.pageSearchError = `No hay navegaciones que incluyan ${cleanPath}.`;
          }

          this.pageSearchLoading = false;
        },
        error: (error) => {
          console.error('Error buscando navegaciones por página:', error);
          this.pageSearchError =
            'No se pudieron buscar navegaciones por esa página.';
          this.pageSearchLoading = false;
        },
      });
  }

  openVisitorTrace(visitor: VisitorExplorerItem): void {
    if (!visitor) return;

    const visitorId = visitor.visitorId;
    const visitorKey = visitor.visitorKey;

    if (!visitorId && !visitorKey) {
      this.traceError = 'No hay visitorId ni visitorKey para abrir la traza.';
      return;
    }

    this.loadVisitorTrace({
      visitorId,
      visitorKey,
    });
  }

  openRelevantSessionTrace(session: any): void {
    if (!session) return;

    this.loadVisitorTrace({
      visitorId: session.visitorId,
      visitorKey: session.visitorKey,
    });
  }

  closeTraceModal(): void {
    this.traceModalOpen = false;
    this.traceLoading = false;
    this.traceError = '';
    this.selectedTrace = null;
  }

  loadVisitorTrace(payload: { visitorId?: string; visitorKey?: string }): void {
    this.traceLoading = true;
    this.traceError = '';
    this.selectedTrace = null;
    this.traceModalOpen = true;

    const params: {
      visitorId?: string;
      visitorKey?: string;
      from: string;
      to: string;
      limit: number;
      sessionTimeoutMinutes: number;
    } = {
      from: this.from,
      to: this.to,
      limit: 500,
      sessionTimeoutMinutes: 30,
    };

    if (payload.visitorId && payload.visitorId !== 'unknown') {
      params.visitorId = payload.visitorId;
    }

    if (
      (!payload.visitorId || payload.visitorId === 'unknown') &&
      payload.visitorKey
    ) {
      params.visitorKey = payload.visitorKey;
    }

    this.adminVisitorsService
      .getVisitorNavigationTrace(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.selectedTrace = response.data;
          this.traceLoading = false;

          if (!response.data?.found) {
            this.traceError =
              response.data?.message ||
              'No hay navegación registrada para este visitante.';
          }
        },
        error: (error) => {
          console.error('Error cargando traza del visitante:', error);
          this.traceError = 'No se pudo cargar la traza de navegación.';
          this.traceLoading = false;
        },
      });
  }

  getFilteredVisitors(): VisitorExplorerItem[] {
    if (this.pageSearchActive) {
      return this.pageSearchResults;
    }

    const term = this.visitorSearchTerm.trim().toLowerCase();

    if (!term) {
      return this.allVisitors;
    }

    return this.allVisitors.filter((visitor) => {
      return (
        visitor.visitorId?.toLowerCase().includes(term) ||
        visitor.visitorKey?.toLowerCase().includes(term) ||
        visitor.lastPath?.toLowerCase().includes(term) ||
        visitor.source?.toLowerCase().includes(term) ||
        visitor.device?.toLowerCase().includes(term)
      );
    });
  }

  onVisitorSearchTermChange(): void {
    const term = this.visitorSearchTerm.trim();

    if (!term) {
      this.resetPageSearch();
      this.traceError = '';
      return;
    }

    if (!this.isPageSearchTerm(term) && this.pageSearchActive) {
      this.resetPageSearch();
    }
  }

  clearVisitorSearch(): void {
    this.visitorSearchTerm = '';
    this.resetPageSearch();
    this.traceError = '';
  }

  resetPageSearch(): void {
    this.pageSearchActive = false;
    this.pageSearchLoading = false;
    this.pageSearchLabel = '';
    this.pageSearchResults = [];
    this.pageSearchError = '';
  }

  isPageSearchTerm(value: string): boolean {
    const term = String(value || '').trim();

    if (!term) return false;

    return (
      term.startsWith('/') ||
      term.startsWith('http://') ||
      term.startsWith('https://') ||
      term.includes('dashboard-') ||
      term.includes('property-details') ||
      term.includes('single-v') ||
      term.includes('blogs/') ||
      term.includes('blog/') ||
      term.includes('cms/')
    );
  }

  normalizePageSearchTerm(value: string): string {
    return String(value || '')
      .trim()
      .replace(/^https?:\/\/[^/]+/i, '')
      .replace(/[?#].*$/, '')
      .trim();
  }

  getExplorerPath(visitor: VisitorExplorerItem): string {
    return (
      (visitor as PageSearchVisitor).matchedPath ||
      visitor.lastPath ||
      visitor.firstPath ||
      '-'
    );
  }

  getExplorerDate(visitor: VisitorExplorerItem): string | null | undefined {
    return (
      (visitor as PageSearchVisitor).lastMatchedAt ||
      visitor.lastSeenAt ||
      (visitor as PageSearchVisitor).firstMatchedAt ||
      visitor.firstSeenAt
    );
  }

  getExplorerRequests(visitor: VisitorExplorerItem): number {
    return (
      (visitor as PageSearchVisitor).matchesCount ||
      visitor.requestsCount ||
      0
    );
  }

  getExplorerTitle(visitor: VisitorExplorerItem): string {
    return (
      (visitor as PageSearchVisitor).matchedTitle ||
      visitor.lastTitle ||
      visitor.lastPath ||
      visitor.visitorId ||
      '-'
    );
  }

  getBehaviorSummary(): any {
    return this.behaviorInsights?.summary || {};
  }

  getExitsByPath(): any[] {
    return this.behaviorInsights?.exitsByPath || [];
  }

  getTimeByPath(): any[] {
    return this.behaviorInsights?.timeByPath || [];
  }

  getEntryPages(): any[] {
    return this.behaviorInsights?.entryPages || [];
  }

  getRelevantSessions(): any[] {
    return this.behaviorInsights?.relevantSessions || [];
  }

  getSourceQuality(): any[] {
    return this.behaviorInsights?.sourceQuality || [];
  }

  getJourneys(): any[] {
    return this.behaviorInsights?.journeys || [];
  }

  getMaxValue(items: any[], field: string): number {
    const values = (items || []).map((item) => Number(item?.[field] || 0));
    return Math.max(...values, 1);
  }

  getBarWidth(item: any, field: string, items: any[]): number {
    const value = Number(item?.[field] || 0);
    const max = this.getMaxValue(items, field);

    return Math.max((value / max) * 100, 4);
  }

  getPathLabel(path?: string, title?: string): string {
    if (title && title.trim()) {
      return title;
    }

    return this.cleanPath(path);
  }

  getMaxTimelineValue(): number {
    const values = this.timeline.map((item) => item.activeVisitors || 0);
    return Math.max(...values, 1);
  }

  getTimelineHeight(value: number): number {
    const max = this.getMaxTimelineValue();
    return Math.max((value / max) * 100, 4);
  }

  getPathWidth(item: TopVisitorPath): number {
    return Math.min(Math.max(item.percentage || 0, 4), 100);
  }

  formatNumber(value?: number | null): string {
    return new Intl.NumberFormat('es-ES').format(value || 0);
  }

  formatPercent(value?: number | null): string {
    return `${Number(value || 0).toFixed(1)}%`;
  }

  formatDate(value?: string | null): string {
    if (!value) return '-';

    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  formatShortDate(value?: string | null): string {
    if (!value) return '-';

    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
    }).format(new Date(value));
  }

  cleanPath(path?: string): string {
    if (!path) return '-';

    try {
      const withoutQuery = path.split('?')[0];
      return withoutQuery.length > 48
        ? `${withoutQuery.slice(0, 48)}...`
        : withoutQuery;
    } catch {
      return path;
    }
  }

  cleanLongText(value?: string | null, length = 70): string {
    if (!value) return '-';

    return value.length > length ? `${value.slice(0, length)}...` : value;
  }

  getVisitorLabel(visitor: VisitorExplorerItem): string {
    if (visitor.visitorType === 'new') return 'Nuevo';
    if (visitor.visitorType === 'returning') return 'Recurrente';
    return 'Desconocido';
  }

  getVisitorClass(visitor: VisitorExplorerItem): string {
    if (visitor.visitorType === 'new') return 'is-new';
    if (visitor.visitorType === 'returning') return 'is-returning';
    return 'is-unknown';
  }

  trackByVisitorId(_: number, visitor: VisitorExplorerItem): string {
    return visitor.visitorKey || visitor.visitorId;
  }

  trackByTraceStep(_: number, item: any): number {
    return item.order;
  }

private setDefaultDateRange(): void {
  const toDate = new Date();
  const fromDate = new Date();

  // Últimos 7 días desde hoy
  fromDate.setDate(toDate.getDate() - 7);

  this.from = this.toInputDate(fromDate);
  this.to = this.toInputDate(toDate);
}

  private toInputDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }
}